/**
 * Page de gestion du lien avec un cyber professionnel
 * Les clients lambda peuvent activer le suivi avec un cyber de leur choix
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  CheckCircle,
  Cancel,
  Info,
  PersonAdd,
  Link as LinkIcon,
  LinkOff,
  Delete,
} from '@mui/icons-material';
import axios from 'axios';

interface Cyber {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  telephone?: string;
}

interface CyberRelationship {
  id: number;
  status: 'active' | 'pending';
  suivi_active: boolean;
  created_at: string;
  invitation_accepted_at?: string;
  cyber_id: number;
  cyber_nom: string;
  cyber_prenom: string;
  cyber_email: string;
  cyber_telephone?: string;
}

export default function ClientCyberManagement() {
  const navigate = useNavigate();
  const [relationships, setRelationships] = useState<CyberRelationship[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchedCyber, setSearchedCyber] = useState<Cyber | null>(null);
  const [searching, setSearching] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState<'deactivate' | 'remove' | null>(null);
  const [selectedRelationship, setSelectedRelationship] = useState<CyberRelationship | null>(null);

  useEffect(() => {
    fetchCyberStatus();
  }, []);

  const fetchCyberStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('clientToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/client/cyber/status`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setRelationships(response.data.data.relationships || []);
    } catch (error: any) {
      console.error('Erreur chargement statut cyber:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchCyber = async () => {
    if (!searchEmail.trim()) {
      alert('Veuillez entrer un email');
      return;
    }

    try {
      setSearching(true);
      setSearchedCyber(null);
      const token = localStorage.getItem('clientToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/client/cyber/search`,
        { email: searchEmail },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSearchedCyber(response.data.data.cyber);
    } catch (error: any) {
      console.error('Erreur recherche cyber:', error);
      alert(error.response?.data?.message || 'Cyber non trouvé');
    } finally {
      setSearching(false);
    }
  };

  const handleRequestLink = async () => {
    if (!searchedCyber) return;

    try {
      setRequesting(true);
      const token = localStorage.getItem('clientToken');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/client/cyber/request-link`,
        { cyber_id: searchedCyber.id },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      alert('✅ Demande envoyée avec succès au cyber !');
      setSearchEmail('');
      setSearchedCyber(null);
      fetchCyberStatus();
    } catch (error: any) {
      console.error('Erreur demande liaison:', error);
      alert(error.response?.data?.message || 'Erreur lors de la demande');
    } finally {
      setRequesting(false);
    }
  };

  const handleToggleSuivi = async (relationship: CyberRelationship) => {
    try {
      const token = localStorage.getItem('clientToken');
      const endpoint = relationship.suivi_active ? 'deactivate' : 'reactivate';
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/client/cyber/${endpoint}/${relationship.id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      alert(
        relationship.suivi_active
          ? '✅ Suivi désactivé. Le cyber ne peut plus voir vos dossiers.'
          : '✅ Suivi réactivé.'
      );
      fetchCyberStatus();
    } catch (error: any) {
      console.error('Erreur toggle suivi:', error);
      alert(error.response?.data?.message || 'Erreur lors de la modification');
    }
  };

  const handleRemoveLink = async () => {
    if (!selectedRelationship) return;

    try {
      const token = localStorage.getItem('clientToken');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/client/cyber/${selectedRelationship.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      alert('✅ Liaison supprimée définitivement');
      setShowConfirmDialog(false);
      setSelectedRelationship(null);
      fetchCyberStatus();
    } catch (error: any) {
      console.error('Erreur suppression liaison:', error);
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const openConfirmDialog = (relationship: CyberRelationship, action: 'deactivate' | 'remove') => {
    setSelectedRelationship(relationship);
    setActionType(action);
    setShowConfirmDialog(true);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const activeCyber = relationships.find((r) => r.status === 'active' && r.suivi_active);
  const pendingRelationships = relationships.filter((r) => r.status === 'pending');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 4 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
          <IconButton onClick={() => navigate('/client/settings')} edge="start">
            <ArrowBack />
          </IconButton>
          <Box sx={{ ml: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Lien avec un cyber
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Activez le suivi avec un professionnel
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Container maxWidth="md" sx={{ mt: 3, px: 2 }}>
        {/* Info générale */}
        <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Qu'est-ce que le suivi par un cyber ?
          </Typography>
          <Typography variant="caption">
            En vous liant à un cyber professionnel, vous lui permettez de suivre l'évolution de vos
            dossiers ANEF et de vous assister dans vos démarches. Vous gardez le contrôle et pouvez
            désactiver le suivi à tout moment.
          </Typography>
        </Alert>

        {/* Cyber actif */}
        {activeCyber && (
          <Paper sx={{ p: 2, mb: 3, borderLeft: '4px solid', borderColor: 'success.main' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flex: 1 }}>
                Cyber actif
              </Typography>
              <Chip label="Actif" color="success" size="small" />
            </Box>

            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>{activeCyber.cyber_prenom} {activeCyber.cyber_nom}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              📧 {activeCyber.cyber_email}
            </Typography>
            {activeCyber.cyber_telephone && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                📞 {activeCyber.cyber_telephone}
              </Typography>
            )}

            <Divider sx={{ my: 1.5 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={activeCyber.suivi_active}
                  onChange={() => handleToggleSuivi(activeCyber)}
                />
              }
              label={
                <Typography variant="caption">
                  {activeCyber.suivi_active
                    ? 'Suivi actif - Le cyber peut voir vos dossiers'
                    : 'Suivi désactivé'}
                </Typography>
              }
            />

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                color="error"
                startIcon={<Delete />}
                onClick={() => openConfirmDialog(activeCyber, 'remove')}
                fullWidth
              >
                Supprimer la liaison
              </Button>
            </Box>
          </Paper>
        )}

        {/* Demandes en attente */}
        {pendingRelationships.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              Demandes en attente
            </Typography>
            {pendingRelationships.map((rel) => (
              <Card key={rel.id} sx={{ mb: 1 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', flex: 1 }}>
                      {rel.cyber_prenom} {rel.cyber_nom}
                    </Typography>
                    <Chip label="En attente" color="warning" size="small" />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {rel.cyber_email}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Paper>
        )}

        {/* Note: Section de recherche supprimée - Les clients créés par les cybers sont automatiquement liés */}
        {!activeCyber && (
          <Paper sx={{ p: 2 }}>
            <Alert severity="info" icon={<Info />}>
              <Typography variant="body2">
                <strong>Aucun cyber associé</strong>
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Si vous avez été créé par un cyber professionnel, la liaison se fait automatiquement.
                Vous n'avez aucune action à effectuer.
              </Typography>
            </Alert>
          </Paper>
        )}
      </Container>

      {/* Dialog de confirmation */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>
          {actionType === 'remove' ? 'Supprimer la liaison ?' : 'Désactiver le suivi ?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {actionType === 'remove'
              ? 'Êtes-vous sûr de vouloir supprimer définitivement cette liaison ? Le cyber ne pourra plus voir vos dossiers.'
              : 'Le cyber ne pourra plus voir vos dossiers tant que vous ne réactiverez pas le suivi.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Annuler</Button>
          <Button
            color="error"
            onClick={actionType === 'remove' ? handleRemoveLink : () => {}}
            autoFocus
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
